import {
    isValidElement,
    type ReactNode,
    cloneElement,
    Fragment,
    Children
} from "react";
import {
    Platform
} from "react-native";
import type INavigationMenuItemProps from "./components/item/type";
import type {
    NavigationMenuContentWidthConstantType,
    NavigationMenuPanelFocusConstantType,
    NavigationMenuBarOffsetConstantType,
    NavigationMenuContentIdConstantType,
    NavigationMenuPanelKeyConstantType,
    NavigationMenuItemElementType,
    NavigationMenuItemMetaType
} from "./type";
import {
    getPopoverTabbables,
    focusPopoverElement,
    POPOVER_SIZE_RATIOS,
    getPopoverElement
} from "../popover/util";
import {
    getHorizontalStep
} from "../../helpers/direction";
import {
    getHeaderHeight
} from "../header/stylesheet";

export const NAVIGATION_MENU_TRACK_DATA_KEY = "ncoreNavTrack";

export const NAVIGATION_MENU_TRACK_SELECTOR = "[data-ncore-nav-track]";

export const NAVIGATION_MENU_FULL_WIDTH_TRACKS = 3;

export const NAVIGATION_MENU_FOCUS_RETRY_LIMIT = 10;

const NAVIGATION_MENU_WHITESPACE_PATTERN = /\s+/g;

export const getNavigationMenuItems = (children: ReactNode, keyPrefix = ""): Array<NavigationMenuItemElementType> => {
    return Children.toArray(children).flatMap((child) => {
        if(!isValidElement<{ children?: ReactNode }>(child)) {
            return [];
        }

        const childKey = `${keyPrefix}${child.key ?? ""}`;

        if(child.type === Fragment) {
            return getNavigationMenuItems(child.props.children, `${childKey}/`);
        }

        return [
            cloneElement(child, {
                key: childKey
            }) as NavigationMenuItemElementType
        ];
    });
};

export const getNavigationMenuItemKey = ({
    title,
    value,
    href
}: Pick<INavigationMenuItemProps, "value" | "title" | "href">): string => {
    return value ?? href ?? title;
};

export const getNavigationMenuHasContent = ({
    children,
    href
}: Pick<INavigationMenuItemProps, "children" | "href">): boolean => {
    return href === undefined && children !== undefined && children !== null;
};

export const getNavigationMenuItemsMeta = (items: Array<NavigationMenuItemElementType>): Array<NavigationMenuItemMetaType> => {
    return items.map((item) => ({
        hasContent: getNavigationMenuHasContent(item.props),
        key: getNavigationMenuItemKey(item.props),
        isDisabled: !!item.props.isDisabled
    }));
};

export const getNavigationMenuTrackCount = (content: ReactNode): number => {
    const [
        contentElement
    ] = Children.toArray(content).filter((child) => isValidElement<{ children?: ReactNode }>(child));

    if(!isValidElement<{ children?: ReactNode }>(contentElement)) {
        return 0;
    }

    return Children.toArray(contentElement.props.children).filter((child) => isValidElement(child)).length;
};

export const getNavigationMenuContentId = ({
    itemKey,
    baseId
}: NavigationMenuContentIdConstantType): string => {
    return `${baseId}-${itemKey.replace(NAVIGATION_MENU_WHITESPACE_PATTERN, "-")}`;
};

export const getNavigationMenuContentWidth = ({
    shapeConfigs,
    size
}: NavigationMenuContentWidthConstantType): number => {
    return shapeConfigs.sideListWidth * POPOVER_SIZE_RATIOS[size];
};

export const getNavigationMenuBarPadding = ({
    controlHeights,
    borders,
    spaces,
    size
}: NavigationMenuBarOffsetConstantType): number => {
    const barHeight = getHeaderHeight({
        controlHeights,
        spaces
    });

    return (barHeight - borders.separator - controlHeights[size]) / 2;
};

export const getNavigationMenuBarOffset = ({
    controlHeights,
    borders,
    spaces,
    size
}: NavigationMenuBarOffsetConstantType): number => {
    return getNavigationMenuBarPadding({
        controlHeights,
        borders,
        spaces,
        size
    }) + borders.separator;
};

export const focusNavigationMenuPanel = ({
    surfaceRef
}: NavigationMenuPanelFocusConstantType): boolean => {
    const surfaceElement = getPopoverElement(surfaceRef.current);

    if(Platform.OS !== "web" || !surfaceElement) {
        return false;
    }

    const [
        firstElement
    ] = getPopoverTabbables(surfaceElement);

    if(!firstElement) {
        return false;
    }

    focusPopoverElement(firstElement);

    return true;
};

export const handleNavigationMenuPanelKeyDown = ({
    onFocusTrigger,
    surfaceRef,
    direction,
    event
}: NavigationMenuPanelKeyConstantType) => {
    const surfaceElement = getPopoverElement(surfaceRef.current);

    if(Platform.OS !== "web" || !surfaceElement) {
        return;
    }

    const tabbables = getPopoverTabbables(surfaceElement);

    const {
        activeElement
    } = surfaceElement.ownerDocument;

    const index = activeElement instanceof HTMLElement ? tabbables.indexOf(activeElement) : -1;

    const handleFocusAt = (nextIndex: number) => {
        const nextElement = tabbables[nextIndex];

        if(!nextElement) {
            return;
        }

        event.preventDefault();

        focusPopoverElement(nextElement);
    };

    if(event.key === "ArrowDown") {
        handleFocusAt(index + 1);

        return;
    }

    if(event.key === "ArrowUp") {
        if(index > 0) {
            handleFocusAt(index - 1);

            return;
        }

        event.preventDefault();

        onFocusTrigger();

        return;
    }

    if(event.key === "Home") {
        handleFocusAt(0);

        return;
    }

    if(event.key === "End") {
        handleFocusAt(tabbables.length - 1);

        return;
    }

    const step = getHorizontalStep({
        key: event.key,
        direction
    });

    if(step === 0 || !(activeElement instanceof HTMLElement)) {
        return;
    }

    const tracks = Array.from(surfaceElement.querySelectorAll<HTMLElement>(NAVIGATION_MENU_TRACK_SELECTOR));

    const currentTrack = tracks.findIndex((track) => track.contains(activeElement));

    const nextTrack = tracks[currentTrack + step];

    if(currentTrack < 0 || !nextTrack) {
        return;
    }

    const [
        firstElement
    ] = getPopoverTabbables(nextTrack);

    if(!firstElement) {
        return;
    }

    event.preventDefault();

    focusPopoverElement(firstElement);
};
